import { DatepickerRangeModel, DatepickerRangeOutput } from '../../../../../common/models';
import { TruckStatus, UliActivityType, UliOrigin, UliStatus } from '../../../../../common';
import { AllocationViewAvailableDocksModel } from './allocation-view-available-docks.model';
import { AllocationViewFilterName, AllocationViewListRangeDate } from '../constants';
import { DraggableFilterType } from '../../../../constants/filter.constant';
import { FormBuilder, FormControl } from '@angular/forms';
import { endOfDay, startOfDay } from 'date-fns';

export const AllocationViewListDateRangeFilter = {
  ...AllocationViewListRangeDate,
};

export type AllocationViewListDateRangeFilter = AllocationViewListRangeDate;

export type AllocationViewListGroupTimestampValues = {
  [key in AllocationViewListDateRangeFilter]: DatepickerRangeModel;
};

export type AllocationViewListGroupDateValues = {
  [key in AllocationViewListDateRangeFilter]: DatepickerRangeOutput;
};

export const ALLOCATION_VIEW_FILTER_TYPE_BY_NAME: Map<AllocationViewFilterName, DraggableFilterType> = new Map([
  [AllocationViewFilterName.CarrierId, DraggableFilterType.Input],
  [AllocationViewFilterName.CarrierNumber, DraggableFilterType.Input],
  [AllocationViewFilterName.LoadNumbers, DraggableFilterType.ChipsInput],
  [AllocationViewFilterName.TransportOrderNumbers, DraggableFilterType.ChipsInput],
  [AllocationViewFilterName.DocumentNumbers, DraggableFilterType.ChipsInput],
  [AllocationViewFilterName.NoDock, DraggableFilterType.BorderedCheckbox],
  [AllocationViewFilterName.WithoutTruckPlate, DraggableFilterType.BorderedCheckbox],
  [AllocationViewFilterName.Type, DraggableFilterType.MultipleSelect],
  [AllocationViewFilterName.TruckPlate, DraggableFilterType.UppercaseInput],
  [AllocationViewFilterName.Party, DraggableFilterType.Input],
  [AllocationViewFilterName.Uli, DraggableFilterType.Input],
  [AllocationViewFilterName.UliOrigin, DraggableFilterType.MultipleSelect],
  [AllocationViewFilterName.UliStatuses, DraggableFilterType.MultipleSelect],
  [AllocationViewFilterName.TruckStatuses, DraggableFilterType.MultipleSelect],
  [AllocationViewFilterName.CompartmentDockStructure, DraggableFilterType.CheckboxListPopup],
  [AllocationViewFilterName.ParkingSpaceId, DraggableFilterType.Input],
]);

const TRUCK_STATUS_FILTERS: Exclude<TruckStatus, TruckStatus.DepartureSecurityFinished>[] = [
  TruckStatus.Init,
  TruckStatus.Left,
  TruckStatus.WaitingArea,
  TruckStatus.Registered,
  TruckStatus.CalledToDock,
  TruckStatus.CalledToArrivalSecurity,
  TruckStatus.ArrivalSecurityFinished,
  TruckStatus.ArrivedAtDock,
  TruckStatus.ReleasedFromDock,
  TruckStatus.CalledToSealPictureControl,
  TruckStatus.SealPictureControlFinished,
  TruckStatus.PreparedForDeparture,
  TruckStatus.Departed,
  TruckStatus.Geofence1,
  TruckStatus.Geofence2,
  TruckStatus.CalledToWaitingArea,
];

export const ALLOCATION_VIEW_FILTER_SELECT_OPTIONS_BY_NAME = new Map<AllocationViewFilterName, string[]>([
  [AllocationViewFilterName.UliOrigin, Object.values(UliOrigin)],
  [AllocationViewFilterName.UliStatuses, Object.values(UliStatus)],
  [AllocationViewFilterName.TruckStatuses, TRUCK_STATUS_FILTERS],
  [AllocationViewFilterName.Type, Object.values(UliActivityType)],
]);

export class AllocationViewListFiltersBaseModel {
  [AllocationViewFilterName.NoDock] = false;
  [AllocationViewFilterName.WithoutTruckPlate] = false;
  [AllocationViewFilterName.CarrierId]: string = null;
  [AllocationViewFilterName.CarrierNumber]: string = null;
  [AllocationViewFilterName.LoadNumbers]: string[] = [];
  [AllocationViewFilterName.TransportOrderNumbers]: string[] = [];
  [AllocationViewFilterName.DocumentNumbers]: string[] = [];
  [AllocationViewFilterName.Type]: UliActivityType[] = [];
  [AllocationViewFilterName.TruckPlate]: string = null;
  [AllocationViewFilterName.Party]: string = null;
  [AllocationViewFilterName.Uli]: string = null;
  [AllocationViewFilterName.UliStatuses]: UliStatus[] = [];
  [AllocationViewFilterName.UliOrigin]: UliOrigin[] = [];
  [AllocationViewFilterName.TruckStatuses]: TruckStatus[] = [];
  [AllocationViewFilterName.SlotId]: number = null;
  [AllocationViewFilterName.CompartmentDockStructure]: AllocationViewAvailableDocksModel | string[] = {
    compartments: [],
  };
  [AllocationViewFilterName.RegistrationId]: number = null;
  [AllocationViewFilterName.ParkingSpaceId]: string = null;
}

export class AllocationViewFiltersModel extends AllocationViewListFiltersBaseModel {
  [AllocationViewFilterName.TimeFrame]: DatepickerRangeModel = new DatepickerRangeModel({
    start: startOfDay(new Date()),
    end: endOfDay(new Date()),
  });

  constructor(params?: AllocationViewFiltersModel) {
    super();
    if (params) {
      Object.assign(this, params);
    }
  }
}

export class AllocationViewListFiltersModelOutput extends AllocationViewListFiltersBaseModel {
  [AllocationViewFilterName.TimeFrame] = new DatepickerRangeOutput();

  constructor(params?: AllocationViewFiltersModel) {
    super();
    if (params) {
      Object.assign(this, params);
    }
  }
}

export class AllocationViewListFiltersForm {
  constructor(
    model: AllocationViewFiltersModel,
    fb: FormBuilder = new FormBuilder(),
    public timeFrame: FormControl<DatepickerRangeModel> = fb.control<DatepickerRangeModel>(
      model[AllocationViewFilterName.TimeFrame],
      [],
    ),
    public withoutTruckPlate: FormControl<boolean> = fb.control<boolean>(
      model[AllocationViewFilterName.WithoutTruckPlate],
      [],
    ),
    public noDock: FormControl<boolean> = fb.control<boolean>(model[AllocationViewFilterName.NoDock], []),
    public loadNumbers: FormControl<string[]> = fb.control<string[]>(model[AllocationViewFilterName.LoadNumbers], []),
    public transportOrderNumbers: FormControl<string[]> = fb.control<string[]>(
      model[AllocationViewFilterName.TransportOrderNumbers],
      [],
    ),
    public documentNumbers: FormControl<string[]> = fb.control<string[]>(
      model[AllocationViewFilterName.DocumentNumbers],
      [],
    ),
    public carrierId: FormControl<string> = fb.control<string>(model[AllocationViewFilterName.CarrierId], []),
    public carrierNumber: FormControl<string> = fb.control<string>(model[AllocationViewFilterName.CarrierNumber], []),
    public truckPlate: FormControl<string> = fb.control<string>(model[AllocationViewFilterName.TruckPlate], []),
    public type: FormControl<UliActivityType[]> = fb.control<UliActivityType[]>(
      model[AllocationViewFilterName.Type],
      [],
    ),
    public party: FormControl<string> = fb.control<string>(model[AllocationViewFilterName.Party], []),
    public uli: FormControl<string> = fb.control<string>(model[AllocationViewFilterName.Uli], []),
    public uliOrigin: FormControl<UliOrigin[]> = fb.control<UliOrigin[]>(model[AllocationViewFilterName.UliOrigin], []),
    public uliStatuses: FormControl<UliStatus[]> = fb.control<UliStatus[]>(
      model[AllocationViewFilterName.UliStatuses],
      [],
    ),
    public truckStatuses: FormControl<TruckStatus[]> = fb.control<TruckStatus[]>(
      model[AllocationViewFilterName.TruckStatuses],
      [],
    ),
    public slotId: FormControl<number> = fb.control<number>(model[AllocationViewFilterName.SlotId], []),
    public registrationId: FormControl<number> = fb.control<number>(model[AllocationViewFilterName.RegistrationId], []),
    public parkingSpaceId: FormControl<string> = fb.control<string>(model[AllocationViewFilterName.ParkingSpaceId], []),
    public compartmentDockStructure: FormControl<AllocationViewAvailableDocksModel | string[]> = fb.control<
      AllocationViewAvailableDocksModel | string[]
    >(model[AllocationViewFilterName.CompartmentDockStructure], []),
  ) {}
}
